import tenantConstants from '@constants';
import tenantTheme from '@theme';
import { Input, List, Space, Typography } from 'antd';
import cx from 'clsx';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Flex,
  Heading,
  Icon,
  Skeleton,
  TextWithIcon,
  notification,
} from '../../../../../components/common';
import { IconStyled } from '../../../../../components/common/icon/IconStyled';
import { useRouteNavigate } from '../../../../../hooks';
import { DATE_FORMAT } from '../../../../../constants/formats';
import { regex } from '../../../../../constants/regex';
import { getTimeDateString } from '../../../../../utility/date';
import { convertQueryObjToString } from '../../../../../utility/urlQuery';
import { formatNumberString } from '../../../../../utility/utility';
import {
  QUOTA_CART_SOURCE,
  canBuyQuota,
  getTopUpCartLines,
  getTopUpTotal,
  splitTopUpProducts,
} from '../../../../../utility/quota';
import { CreditTopUpCard } from '../styled';
import { useCreateCartMutation } from '../../../../../apis/cart';
import { useGetQuotaCreditsWidgetDataQuery, useGetQuotaPricesQuery } from '../../../../../apis/quotaCredits';

const { Text } = Typography;

// Product top-up for quota users: the credit top-up card with one quantity per product. Prices include VAT and
// the units expire with the client's active package.
const QuotaTopUps = ({ redirectUrl }) => {
  const { t } = useTranslation();
  const navigate = useRouteNavigate();
  const isMobile = useSelector((state) => state?.app?.AppConfig?.isMobile);
  const user = useSelector((state) => state?.app?.loginUser?.user);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [viewAll, setViewAll] = useState(false);

  const canBuy = canBuyQuota(user);
  const { data: products = [], isLoading: pricesLoading } = useGetQuotaPricesQuery(undefined, { skip: !canBuy });
  const {
    data: summary,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    error: summaryError,
    refetch: refetchSummary,
  } = useGetQuotaCreditsWidgetDataQuery(
    { platform_id: [user?.platforms?.[0]?.id] },
    { skip: !canBuy },
  );
  const [createCart] = useCreateCartMutation();

  const { shown, more } = useMemo(() => splitTopUpProducts(products), [products]);
  const total = useMemo(() => getTopUpTotal(products, quantities), [products, quantities]);
  const hasPackage = !!summary?.package;

  if (!canBuy) return null;

  const onQuantityChange = (productId, value) => {
    if (value === '' || regex.wholeNumbers.test(value)) {
      setQuantities((prev) => ({ ...prev, [productId]: value === '' ? '' : parseInt(value, 10) }));
    }
  };

  const handleCheckout = async () => {
    const lines = getTopUpCartLines(products, quantities);
    setLoading(true);
    const response = await createCart({
      cart: { source: QUOTA_CART_SOURCE, listing_id: null, listing_expiry_days: null, cart_details_attributes: lines },
    });
    setLoading(false);
    if (response?.error) {
      notification.error(response.error);
      return;
    }
    navigate(
      `/checkout?${convertQueryObjToString({ cart_id: response?.data?.cart?.id, ...(redirectUrl && { redirectUrl }) })}`,
    );
  };

  const renderItem = (item) => (
    <List.Item className="px-0">
      <Space>
        <IconStyled
          color={tenantTheme['white-color']}
          style={{
            '--icon-bg-color': item?.iconColor,
            '--icon-styled-width': '16px',
            height: 'var(--icon-styled-width)',
          }}
        >
          <Icon icon="GoDotFill" size={10} />
        </IconStyled>
        <div>
          <div className="fw-600">{t(item?.name)}</div>
          <Text type="secondary" className="fz-12">
            {tenantConstants?.CURRENCY_SYMBOL?.()} {formatNumberString(item?.price, false)} {t('each')}
          </Text>
        </div>
      </Space>
      <Input
        className="fw-600"
        style={{ width: isMobile ? 80 : 120 }}
        placeholder="0"
        value={quantities?.[item?.id] ?? ''}
        disabled={loading}
        maxLength={4}
        onChange={(e) => onQuantityChange(item?.id, e?.target?.value)}
      />
    </List.Item>
  );

  return (
    <CreditTopUpCard className="topUpCard" styles={{ body: { padding: isMobile ? 16 : 24 } }}>
      <Flex vertical gap="16px" className={isMobile && 'mb-16'}>
        <Flex justify="space-between" align="start" gap="12px">
          <div>
            <Heading as={isMobile ? 'h6' : 'h5'} style={{ fontWeight: '700' }} className="mb-4">
              {t('Product Top-up')}
            </Heading>
            <Text type="secondary" className={isMobile && 'fz-12'}>
              {t('Ran out of a product? Add more on top of your package.')}
            </Text>
          </div>
          {hasPackage && !!more.length && (
            <Button
              type="link"
              className="p-0"
              icon="SideArrowIcon"
              iconClassName={cx('top-up-toggle-icon', viewAll && 'open')}
              iconPosition="end"
              onClick={() => setViewAll((open) => !open)}
              aria-expanded={viewAll}
              style={{ color: tenantTheme['primary-text-color'], gap: '6px', whiteSpace: 'nowrap', flex: 'none' }}
            >
              {viewAll ? t('View Less') : t('View All')}
            </Button>
          )}
        </Flex>
        {summaryLoading ? (
          <Skeleton type="paragraph" />
        ) : summaryError ? (
          <EmptyState title="" message={summaryError} onClick={refetchSummary} buttonLoading={summaryFetching} />
        ) : !hasPackage ? (
          <Alert
            type="info"
            showIcon
            message={t('Top-up is available while you have an active package. Contact your account manager.')}
          />
        ) : (
          // space under the last divider before the total (phones already get it from the card's mb-16)
          <div className={cx(!isMobile && 'mb-16')}>
            <List
              loading={pricesLoading}
              dataSource={shown}
              renderItem={renderItem}
              // antd drops the last row's divider; keep it when the add-ons follow
              className={cx(!!more.length && 'top-up-shown')}
            />
            {!!more.length && (
              // expands in place: height then fade
              <div
                className="top-up-more"
                aria-hidden={!viewAll}
                style={{
                  maxHeight: viewAll ? '1000px' : '0',
                  opacity: viewAll ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.4s ease-in-out, opacity 0.3s ease-in-out',
                }}
              >
                <List dataSource={more} renderItem={renderItem} />
              </div>
            )}
          </div>
        )}
      </Flex>
      {hasPackage && (
        <Card
          className="creditCard"
          bodyStyle={{ padding: '12px 16px' }}
          style={{ backgroundColor: tenantTheme['primary-light-4'], borderWidth: 0 }}
        >
          <Flex vertical={!!isMobile} justify="space-between" gap="20px">
            <TextWithIcon
              textSize={isMobile && '12px'}
              iconProps={{ color: tenantTheme['primary-color'], size: '1em' }}
              icon="IoTimeOutline"
              value={t('Prices include VAT · Expires with your package ({{date}})', {
                date: getTimeDateString(summary?.package?.end_date, DATE_FORMAT, true),
              })}
            />
            <Flex
              justify={isMobile ? 'space-between' : undefined}
              align="center"
              gap="40px"
              className={isMobile ? 'w-100' : undefined}
            >
              <div>
                <Text type="secondary" className="d-block fz-14 fw-700">
                  {t('Total')}
                </Text>
                <Text
                  className={cx(isMobile ? 'fz-14' : 'fz-16', 'fw-700')}
                  style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                  <span className="fw-400">{tenantConstants?.CURRENCY_SYMBOL?.()}</span>
                  {formatNumberString(total, false)}
                </Text>
              </div>
              <Button
                onClick={handleCheckout}
                loading={loading}
                disabled={loading || total <= 0}
                style={{ minWidth: isMobile ? '120px' : '187px' }}
                size={isMobile ? 'small' : 'large'}
                type="primary"
              >
                {t('Proceed to Payment')}
              </Button>
            </Flex>
          </Flex>
        </Card>
      )}
    </CreditTopUpCard>
  );
};

export default QuotaTopUps;
